// @ts-nocheck
if (!customElements.get("configurator-image")) {
  class ConfiguratorImage extends HTMLElement {
    static Selectors = {
      left: "[js-left]",
      center: "[js-center]",
      right: "[js-right]",
      background: "[js-background]",
    };

    constructor() {
      super();

      this.innerPadding = 55;
      this.handleChangeRef = this.handleChange.bind(this);

      this.backgroundMetrics = { leftX: 0, rightX: 0 };
      this.safeMargin = 40;
      this.backgroundSize = 50;
    }

    async canvasDraw() {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

      this.drawBackground();
      const offsetLeft = await this.drawLeft();
      const offsetRight = await this.drawRight();
      this.drawCenter();

      this.drawBorders(offsetLeft, offsetRight);
    }

    drawBackground() {
      const img = this.background.querySelector("img");
      if (!img) return;
      const h = this.backgroundSize;
      const w = img.naturalWidth * (h / img.naturalHeight);

      const x = (this.canvas.width - w) / 2;
      const y = (this.canvas.height - h) / 2;

      this.backgroundMetrics = {
        leftX: x,
        rightX: x + w,
      };

      this.ctx.drawImage(img, x, y, w, h);
    }

    drawCenter() {
      const img = this.center.querySelector("img");
      if (!img) return;
      const h = this.backgroundSize;
      const w = img.naturalWidth * (h / img.naturalHeight);
      // Placed exactly where it sits in your image relative to the background
      this.ctx.drawImage(
        img,
        (this.canvas.width - w) / 2,
        (this.canvas.height - h) / 2,
        w,
        h,
      );
    }

    async drawLeft() {
      const img = this.left.querySelector("img");
      if (!img) return 0;

      const scale = Number(img.getAttribute("data-scale") || 100) / 100;
      const metrics = await this.getFittingMetrics(img.src, "left", this.backgroundSize);
      const h = metrics.suggestedRenderHeight * scale;
      const w = img.naturalWidth * (h / img.naturalHeight);
      let l = this.backgroundMetrics.leftX - w;

      if (l <= this.safeMargin - 10) l = this.safeMargin;
      l += 5;

      this.ctx.drawImage(img, l, (this.canvas.height - h) / 2, w, h);
      return l + (metrics.lineOffsetPct / 100) * w;
    }

    async drawRight() {
      const img = this.right.querySelector("img");
      if (!img) return 0;

      const scale = Number(img.getAttribute("data-scale") || 100) / 100;
      const metrics = await this.getFittingMetrics(img.src, "right", this.backgroundSize);
      const h = metrics.suggestedRenderHeight * scale;
      const w = img.naturalWidth * (h / img.naturalHeight);
      let x = this.backgroundMetrics.rightX;

      const rightEdge = x + w;
      const maxAllowedX = this.canvas.width - this.safeMargin;

      if (rightEdge > maxAllowedX) {
        x = maxAllowedX - w;
      }

      x -= 5;

      this.ctx.save();
      if (img.classList.contains("is-inverted")) {
        this.ctx.scale(-1, 1);
        this.ctx.drawImage(img, -x - w, (this.canvas.height - h) / 2, w, h);
      } else {
        this.ctx.drawImage(img, x, (this.canvas.height - h) / 2, w, h);
      }
      this.ctx.restore();

      return x + (metrics.lineOffsetPct / 100) * w;
    }

    drawBorders(offsetLeft = 0, offsetRight = 0) {
      this.ctx.strokeStyle = "#d7d7d8";
      this.ctx.lineWidth = 1;

      const finalLeftX =
        (offsetLeft && offsetLeft > 0 ? offsetLeft : this.innerPadding) + 0.5;

      const finalRightX =
        (offsetRight && offsetRight > 0
          ? offsetRight
          : this.canvas.width - this.innerPadding) - 0.5;

      this.ctx.beginPath();
      this.ctx.moveTo(finalLeftX, 10);
      this.ctx.lineTo(finalLeftX, this.canvas.height - 40);
      this.ctx.stroke();

      this.ctx.beginPath();
      this.ctx.moveTo(finalRightX, 10);
      this.ctx.lineTo(finalRightX, this.canvas.height - 40);
      this.ctx.stroke();

      this.style.setProperty(
        "--border-left-position",
        (finalLeftX / this.canvas.width) * 100 + "%",
      );
      this.style.setProperty(
        "--border-right-position",
        100 - (finalRightX / this.canvas.width) * 100 + "%",
      );
    }

    isBackground(r, g, b, a) {
      const threshold = 245; // Adjust 0-255 based on how "pale" it is
      const isTransparent = a < 10;
      const isWhite = r > threshold && g > threshold && b > threshold;

      return isTransparent || isWhite;
    }

    async getFittingMetrics(imgUrl = "", side = "left", pipeHeightPx) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = imgUrl;
      await img.decode();

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      const w = canvas.width;
      const h = canvas.height;

      let firstX = -1,
        lastX = -1;
      for (let x = 0; x < w; x++) {
        for (let y = 0; y < h; y++) {
          if (data[(y * w + x) * 4 + 3] > 20) {
            if (firstX === -1) firstX = x;
            lastX = x;
            break;
          }
        }
      }

      const offset = 10;
      const connectionX = side === "left" ? lastX - offset : firstX + offset;
      let lineTarget = side === "left" ? firstX : lastX;

      let maxHeight = 0;
      let peakX = lineTarget;
      let hasHole = false;

      const scanWidth = (lastX - firstX) * 0.6;
      const startX = side === "left" ? firstX : lastX;
      const endX = side === "left" ? firstX + scanWidth : lastX - scanWidth;
      const step = side === "left" ? 1 : -1;

      for (let x = startX; side === "left" ? x < endX : x > endX; x += step) {
        let top = -1,
          bottom = -1;
        for (let y = 0; y < h; y++) {
          if (data[(y * w + x) * 4 + 3] > 20) {
            if (top === -1) top = y;
            bottom = y;
          }
        }
        if (top !== -1) {
          const colH = bottom - top;
          if (colH > maxHeight) {
            maxHeight = colH;
            peakX = x;
            const midAlpha = data[(Math.floor(top + colH / 2) * w + x) * 4 + 3];
            hasHole = midAlpha < 20;
          }
        }
      }
      lineTarget = hasHole ? peakX : side === "left" ? firstX : lastX;

      let topY = -1,
        bottomY = -1;
      for (let y = 0; y < h; y++) {
        if (
          !this.isBackground(
            data[(y * w + connectionX) * 4],
            data[(y * w + connectionX) * 4 + 1],
            data[(y * w + connectionX) * 4 + 2],
            data[(y * w + connectionX) * 4 + 3],
          )
        ) {
          if (topY === -1) topY = y;
          bottomY = y;
        }
      }

      const connectionThickness = bottomY - topY;
      const finalRenderHeight =
        pipeHeightPx * (img.naturalHeight / connectionThickness);

      return {
        lineOffsetPct: (lineTarget / w) * 100,
        connectionPct: (connectionX / w) * 100,
        suggestedRenderHeight: finalRenderHeight,
        connectionThickness: connectionThickness,
      };
    }

    /**
     * @param {Element | null | undefined} container
     * @param {ConfiguratorImageEvent} event
     * @param {Boolean} isInverted
     */
    updateImage(container, event, isInverted = false) {
      if (!container) return;

      const image = document.createElement("img");
      image.src = event.detail.url;
      image.setAttribute("data-id", event.detail.productId);

      image.classList.toggle("is-inverted", isInverted);

      if (event.detail.height) {
        image.setAttribute("style", `max-height: ${event.detail.height}vw;`);
      }

      if (event.detail.scale) {
        image.setAttribute("data-scale", event.detail.scale);
      }

      const currentImage = container.querySelector(
        `[data-id="${event.detail.productId}"]`,
      );
      if (!currentImage) {
        container.appendChild(image);

        if (event.detail.location == "Left") {
          container.setAttribute(
            "style",
            `margin-left: ${event.detail.offset}vw;`,
          );
        }
        if (event.detail.location == "Right") {
          container.setAttribute(
            "style",
            `margin-right: ${event.detail.offset}vw;`,
          );
        }
      } else {
        currentImage.parentElement?.replaceChild(image, currentImage);
      }

      requestAnimationFrame(() => {
        this.canvasDraw();
      });
      
      setTimeout(() => {
        this.canvasDraw();
      }, 250);
    }

    /**
     * @param {ConfiguratorImageEvent} event
     */
    handleChange(event) {
      switch (event.detail.location.toLowerCase()) {
        case "left":
          this.updateImage(this.left, event);
          break;
        case "center":
          this.updateImage(this.center, event);
          break;
        case "right":
          this.updateImage(this.right, event);
          break;
        case "background":
          this.updateImage(this.background, event);
          break;
        case "both":
          this.updateImage(this.left, event);
          this.updateImage(this.right, event, true);
          break;
        default:
          break;
      }
    }

    disconnectedCallback() {
      document.removeEventListener(
        "configurator-image:change",
        this.handleChangeRef,
      );
    }

    connectedCallback() {
      this.left = this.querySelector(ConfiguratorImage.Selectors.left);
      this.center = this.querySelector(ConfiguratorImage.Selectors.center);
      this.right = this.querySelector(ConfiguratorImage.Selectors.right);
      this.background = this.querySelector(
        ConfiguratorImage.Selectors.background,
      );

      this.canvas = this.querySelector("canvas");

      if (
        !this.left ||
        !this.center ||
        !this.right ||
        !this.background ||
        !this.canvas
      )
        return;

      this.ctx = this.canvas.getContext("2d");
      this.canvasDraw();

      document.addEventListener(
        "configurator-image:change",
        this.handleChangeRef,
      );
    }
  }

  customElements.define("configurator-image", ConfiguratorImage);
}
